/**
 * SchoolHR - Human Resource & Staff Management Module
 * Sub-tabs: Dashboard, Directory, Departments, Designations, Attendance, Leave, Payroll, Performance
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Users, Building2, Briefcase, Clock, CalendarDays, DollarSign, Star, LayoutDashboard,
  Plus, Search, Filter, ChevronRight, Loader2, X, Check, Eye, Settings, Download,
  UserPlus, MoreHorizontal, KeyRound, CheckCircle, AlertTriangle, TrendingUp, ArrowUpDown,
  Phone, Mail, Shield, UserX, UserCheck, FileText, Edit, Sparkles, Cake, FileSignature
} from 'lucide-react';
import { IntelligencePanel, RiskAlertsPanel } from '@/components/school/HRPhaseDPanels';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800', suspended: 'bg-red-100 text-red-800',
  on_leave: 'bg-amber-100 text-amber-800', resigned: 'bg-slate-100 text-slate-800',
  retired: 'bg-blue-100 text-blue-800', disabled: 'bg-red-100 text-red-700',
};

const ROLE_TYPES = ['teacher', 'lecturer', 'admin', 'support', 'finance', 'security', 'transport', 'cleaner', 'management', 'principal'];
const EMP_TYPES = ['permanent', 'contract', 'part_time', 'visiting_lecturer', 'volunteer'];
const EMP_STATUSES = ['active', 'suspended', 'on_leave', 'resigned', 'retired', 'disabled'];
const ATT_STATUSES = ['present', 'late', 'absent', 'half_day', 'holiday', 'on_leave', 'sick'];

const ATT_COLORS = {
  present: 'bg-green-500', late: 'bg-amber-500', absent: 'bg-red-500',
  half_day: 'bg-orange-400', holiday: 'bg-blue-400', on_leave: 'bg-purple-400', sick: 'bg-pink-400',
};

// ============== HR DASHBOARD ==============
const HRDashboard = ({ tenantId }) => {
  const { data: dash, isLoading } = useQuery({
    queryKey: ['hr-dashboard', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/dashboard`),
  });
  const seedMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/hr/seed-defaults`, { method: 'POST' }),
    onSuccess: (d) => toast.success(`Seeded: ${d.created.departments} depts, ${d.created.designations} designations, ${d.created.leave_types} leave types`),
  });

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
  const d = dash || {};

  const stats = [
    { label: 'Total Staff', value: d.total_staff, icon: Users, color: 'text-blue-600 bg-blue-50' },
    { label: 'Active', value: d.active_staff, icon: UserCheck, color: 'text-green-600 bg-green-50' },
    { label: 'On Leave', value: d.on_leave, icon: CalendarDays, color: 'text-amber-600 bg-amber-50' },
    { label: 'Departments', value: d.departments, icon: Building2, color: 'text-purple-600 bg-purple-50' },
    { label: 'Pending Leaves', value: d.pending_leaves, icon: Clock, color: 'text-red-600 bg-red-50' },
    { label: 'Present Today', value: d.today_present, icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50' },
  ];

  return (
    <div className="space-y-6" data-testid="hr-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">HR Dashboard</h2>
          <p className="text-sm text-slate-500">Staff overview and quick stats</p>
        </div>
        {d.departments === 0 && (
          <Button size="sm" onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending} data-testid="seed-hr-btn">
            {seedMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
            Setup Defaults
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map(s => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${s.color}`}><s.icon className="h-4 w-4" /></div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{s.value ?? 0}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {Object.keys(d.by_role || {}).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Staff by Role</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(d.by_role).map(([role, count]) => (
                <Badge key={role} variant="secondary" className="text-sm capitalize">{role.replace('_', ' ')}: {count}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== STAFF DIRECTORY ==============
const StaffDirectory = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [viewStaff, setViewStaff] = useState(null);
  const [page, setPage] = useState(1);

  const emptyForm = {
    full_name: '', title: '', gender: '', email: '', phone: '', date_of_birth: '',
    national_id: '', passport_number: '', emergency_contact_name: '', emergency_contact_phone: '',
    emergency_contact_relationship: '', department_id: '', designation_id: '', role_type: 'teacher',
    employment_type: 'permanent', employment_status: 'active', basic_salary: 0,
    date_of_joining: '', teaching_license_number: '', employee_number: '',
    allowances: [], certifications: [], bank_name: '', account_number: '', branch_code: '',
    salary_grade: '', campus_id: '', profile_photo_url: '',
  };
  const [form, setForm] = useState(emptyForm);

  const { data: staffData, isLoading } = useQuery({
    queryKey: ['hr-staff', tenantId, search, deptFilter, roleFilter, statusFilter, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (search) params.set('search', search);
      if (deptFilter) params.set('department_id', deptFilter);
      if (roleFilter) params.set('role_type', roleFilter);
      if (statusFilter) params.set('employment_status', statusFilter);
      return api(`/tenants/${tenantId}/hr/staff?${params}`);
    },
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['hr-departments', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/departments`),
  });

  const { data: designations = [] } = useQuery({
    queryKey: ['hr-designations', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/designations`),
  });

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/hr/staff`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (d) => { toast.success(`Staff created! Employee #${d.employee_number}${d.temp_password ? ` | Temp password: ${d.temp_password}` : ''}`); setShowForm(false); setForm(emptyForm); queryClient.invalidateQueries({ queryKey: ['hr-staff'] }); queryClient.invalidateQueries({ queryKey: ['hr-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api(`/tenants/${tenantId}/hr/staff/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Staff updated'); setShowForm(false); setEditingStaff(null); setForm(emptyForm); queryClient.invalidateQueries({ queryKey: ['hr-staff'] }); },
    onError: (e) => toast.error(e.message),
  });

  const disableMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/hr/staff/${id}/disable`, { method: 'POST' }),
    onSuccess: () => { toast.success('Staff disabled'); queryClient.invalidateQueries({ queryKey: ['hr-staff'] }); queryClient.invalidateQueries({ queryKey: ['hr-dashboard'] }); },
    onError: (e) => toast.error(e.message),
  });

  const enableMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/hr/staff/${id}/enable`, { method: 'POST' }),
    onSuccess: (d) => { toast.success(`Staff enabled. Temp password: ${d.temp_password}`); queryClient.invalidateQueries({ queryKey: ['hr-staff'] }); },
    onError: (e) => toast.error(e.message),
  });

  const openEdit = (s) => {
    setEditingStaff(s);
    setForm({ ...emptyForm, ...s });
    setShowForm(true);
  };

  const staff = staffData?.staff || [];
  const total = staffData?.total || 0;

  return (
    <div className="space-y-4" data-testid="hr-staff-directory">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 w-52" placeholder="Search staff..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} data-testid="staff-search" />
          </div>
          <Select value={deptFilter} onValueChange={v => { setDeptFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={roleFilter} onValueChange={v => { setRoleFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {ROLE_TYPES.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {EMP_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => { setEditingStaff(null); setForm(emptyForm); setShowForm(true); }} data-testid="add-staff-btn">
          <UserPlus className="h-4 w-4 mr-1" /> Add Staff
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Role / Department</TableHead>
              <TableHead>Team Role</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : staff.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No staff found. Click "Add Staff" to get started.</TableCell></TableRow>
            ) : staff.map(s => (
              <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                <TableCell>
                  <div>
                    <p className="font-medium text-slate-900">{s.full_name}</p>
                    <p className="text-xs text-slate-500">{s.employee_number}</p>
                    {s.synced_from && <span className="text-[10px] text-indigo-500">via {s.synced_from}</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <p className="text-sm capitalize">{(s.role_type || '').replace('_', ' ')}</p>
                  <p className="text-xs text-slate-500">{s.department_name || '-'}</p>
                  {s.designation_name && <p className="text-[10px] text-slate-400">{s.designation_name}</p>}
                </TableCell>
                <TableCell>
                  {s.team_role_name ? (
                    <Badge variant="outline" className="text-xs">{s.team_role_name}</Badge>
                  ) : s.team_member_id ? (
                    <span className="text-xs text-slate-400">Linked</span>
                  ) : (
                    <span className="text-xs text-slate-300">-</span>
                  )}
                  {s.teacher_subjects?.length > 0 && (
                    <div className="mt-0.5 flex flex-wrap gap-0.5">{s.teacher_subjects.slice(0, 2).map(sub => <Badge key={sub} className="bg-purple-50 text-purple-600 text-[9px]">{sub}</Badge>)}</div>
                  )}
                </TableCell>
                <TableCell>
                  <p className="text-sm">{s.phone || '-'}</p>
                  <p className="text-xs text-slate-500">{s.email || '-'}</p>
                </TableCell>
                <TableCell><Badge variant="outline" className="text-xs capitalize">{(s.employment_type || '').replace('_', ' ')}</Badge></TableCell>
                <TableCell><Badge className={`text-xs ${STATUS_COLORS[s.employment_status] || 'bg-slate-100 text-slate-800'}`}>{(s.employment_status || '').replace('_', ' ')}</Badge></TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setViewStaff(s)}><Eye className="h-4 w-4 mr-2" /> View Profile</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEdit(s)}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                      {s.employment_status === 'disabled' ? (
                        <DropdownMenuItem onClick={() => enableMutation.mutate(s.id)}><UserCheck className="h-4 w-4 mr-2" /> Re-Enable</DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem className="text-red-600" onClick={() => { if (window.confirm(`Disable ${s.full_name}? This will revoke login and remove timetable allocations.`)) disableMutation.mutate(s.id); }}><UserX className="h-4 w-4 mr-2" /> Disable</DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {total > 50 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm text-slate-500 self-center">Page {page} of {Math.ceil(total / 50)}</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* Staff Form Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) { setShowForm(false); setEditingStaff(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby="staff-form-desc">
          <DialogHeader>
            <DialogTitle>{editingStaff ? 'Edit Staff' : 'Add New Staff'}</DialogTitle>
            <p id="staff-form-desc" className="text-sm text-slate-500">Fill in staff details. Fields marked * are required.</p>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Full Name *</Label><Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="staff-name-input" /></div>
            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Mr, Mrs, Dr, Prof" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="staff-email-input" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
            <div><Label>Gender</Label>
              <Select value={form.gender} onValueChange={v => setForm({...form, gender: v})}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => setForm({...form, date_of_birth: e.target.value})} /></div>
            <div><Label>National ID</Label><Input value={form.national_id} onChange={e => setForm({...form, national_id: e.target.value})} /></div>
            <div><Label>Passport</Label><Input value={form.passport_number} onChange={e => setForm({...form, passport_number: e.target.value})} /></div>
            <div><Label>Department</Label>
              <Select value={form.department_id} onValueChange={v => setForm({...form, department_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>{departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Designation</Label>
              <Select value={form.designation_id} onValueChange={v => setForm({...form, designation_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select designation" /></SelectTrigger>
                <SelectContent>{designations.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Role Type *</Label>
              <Select value={form.role_type} onValueChange={v => setForm({...form, role_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_TYPES.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Employment Type</Label>
              <Select value={form.employment_type} onValueChange={v => setForm({...form, employment_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{EMP_TYPES.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date of Joining</Label><Input type="date" value={form.date_of_joining} onChange={e => setForm({...form, date_of_joining: e.target.value})} /></div>
            <div><Label>Basic Salary</Label><Input type="number" value={form.basic_salary} onChange={e => setForm({...form, basic_salary: parseFloat(e.target.value) || 0})} /></div>
            <div><Label>Teaching License #</Label><Input value={form.teaching_license_number} onChange={e => setForm({...form, teaching_license_number: e.target.value})} /></div>
            <div><Label>Salary Grade</Label><Input value={form.salary_grade} onChange={e => setForm({...form, salary_grade: e.target.value})} /></div>
            <div className="col-span-full border-t pt-4"><Label className="text-base font-semibold">Emergency Contact</Label></div>
            <div><Label>Name</Label><Input value={form.emergency_contact_name} onChange={e => setForm({...form, emergency_contact_name: e.target.value})} /></div>
            <div><Label>Phone</Label><Input value={form.emergency_contact_phone} onChange={e => setForm({...form, emergency_contact_phone: e.target.value})} /></div>
            <div><Label>Relationship</Label><Input value={form.emergency_contact_relationship} onChange={e => setForm({...form, emergency_contact_relationship: e.target.value})} /></div>
            <div className="col-span-full border-t pt-4"><Label className="text-base font-semibold">Bank Details</Label></div>
            <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} /></div>
            <div><Label>Account Number</Label><Input value={form.account_number} onChange={e => setForm({...form, account_number: e.target.value})} /></div>
            <div><Label>Branch Code</Label><Input value={form.branch_code} onChange={e => setForm({...form, branch_code: e.target.value})} /></div>
          </div>
          <DialogFooter className="mt-4">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button
              onClick={() => editingStaff ? updateMutation.mutate({ id: editingStaff.id, data: form }) : createMutation.mutate(form)}
              disabled={!form.full_name || createMutation.isPending || updateMutation.isPending}
              data-testid="save-staff-btn"
            >
              {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editingStaff ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Profile View Dialog */}
      <Dialog open={!!viewStaff} onOpenChange={v => !v && setViewStaff(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="staff-view-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{viewStaff?.full_name}</DialogTitle>
            <p id="staff-view-desc" className="text-sm text-slate-500">{viewStaff?.employee_number} &middot; {(viewStaff?.role_type || '').replace('_', ' ')}</p>
          </DialogHeader>
          {viewStaff && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Department:</span> <strong>{viewStaff.department_name || '-'}</strong></div>
                <div><span className="text-slate-500">Designation:</span> <strong>{viewStaff.designation_name || '-'}</strong></div>
                <div><span className="text-slate-500">Phone:</span> <strong>{viewStaff.phone || '-'}</strong></div>
                <div><span className="text-slate-500">Email:</span> <strong>{viewStaff.email || '-'}</strong></div>
                <div><span className="text-slate-500">Employment:</span> <strong className="capitalize">{(viewStaff.employment_type || '').replace('_', ' ')}</strong></div>
                <div><span className="text-slate-500">Status:</span> <Badge className={STATUS_COLORS[viewStaff.employment_status]}>{viewStaff.employment_status}</Badge></div>
                <div><span className="text-slate-500">Joined:</span> <strong>{viewStaff.date_of_joining || '-'}</strong></div>
                <div><span className="text-slate-500">Salary:</span> <strong>${viewStaff.basic_salary?.toLocaleString() || 0}</strong></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== DEPARTMENTS & DESIGNATIONS ==============
const DepartmentsPanel = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showDeptForm, setShowDeptForm] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', description: '', is_active: true });
  const [showDesForm, setShowDesForm] = useState(false);
  const [editDes, setEditDes] = useState(null);
  const [desForm, setDesForm] = useState({ name: '', department_id: '', level: 1, is_active: true });

  const { data: departments = [], isLoading: dLoading } = useQuery({
    queryKey: ['hr-departments', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/departments`),
  });

  const { data: designations = [], isLoading: desLoading } = useQuery({
    queryKey: ['hr-designations', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/designations`),
  });

  const deptMutation = useMutation({
    mutationFn: (data) => editDept
      ? api(`/tenants/${tenantId}/hr/departments/${editDept.id}`, { method: 'PUT', body: JSON.stringify(data) })
      : api(`/tenants/${tenantId}/hr/departments`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success(editDept ? 'Department updated' : 'Department created'); setShowDeptForm(false); setEditDept(null); setDeptForm({ name: '', description: '', is_active: true }); queryClient.invalidateQueries({ queryKey: ['hr-departments'] }); },
    onError: (e) => toast.error(e.message),
  });

  const desMutation = useMutation({
    mutationFn: (data) => editDes
      ? api(`/tenants/${tenantId}/hr/designations/${editDes.id}`, { method: 'PUT', body: JSON.stringify(data) })
      : api(`/tenants/${tenantId}/hr/designations`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success(editDes ? 'Designation updated' : 'Designation created'); setShowDesForm(false); setEditDes(null); setDesForm({ name: '', department_id: '', level: 1, is_active: true }); queryClient.invalidateQueries({ queryKey: ['hr-designations'] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-6" data-testid="hr-departments-panel">
      {/* Departments */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div><CardTitle className="text-base">Departments</CardTitle><CardDescription>{departments.length} departments</CardDescription></div>
          <Button size="sm" onClick={() => { setEditDept(null); setDeptForm({ name: '', description: '', is_active: true }); setShowDeptForm(true); }} data-testid="add-dept-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </CardHeader>
        <CardContent>
          {dLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {departments.map(d => (
                <div key={d.id} className="border rounded-lg p-3 flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid={`dept-${d.id}`}>
                  <div>
                    <p className="font-medium text-sm">{d.name}</p>
                    <p className="text-xs text-slate-500">{d.staff_count} staff</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {!d.is_active && <Badge variant="outline" className="text-xs text-red-500">Archived</Badge>}
                    <Button variant="ghost" size="sm" onClick={() => { setEditDept(d); setDeptForm({ name: d.name, description: d.description || '', is_active: d.is_active !== false }); setShowDeptForm(true); }}><Edit className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Designations */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div><CardTitle className="text-base">Designations</CardTitle><CardDescription>{designations.length} designations</CardDescription></div>
          <Button size="sm" onClick={() => { setEditDes(null); setDesForm({ name: '', department_id: '', level: 1, is_active: true }); setShowDesForm(true); }} data-testid="add-des-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </CardHeader>
        <CardContent>
          {desLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Staff Count</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {designations.map(d => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell>{d.staff_count}</TableCell>
                    <TableCell>{d.is_active !== false ? <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge> : <Badge variant="outline" className="text-xs text-red-500">Archived</Badge>}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => { setEditDes(d); setDesForm({ name: d.name, department_id: d.department_id || '', level: d.level || 1, is_active: d.is_active !== false }); setShowDesForm(true); }}><Edit className="h-3.5 w-3.5" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dept Dialog */}
      <Dialog open={showDeptForm} onOpenChange={v => !v && setShowDeptForm(false)}>
        <DialogContent aria-describedby="dept-form-desc">
          <DialogHeader><DialogTitle>{editDept ? 'Edit Department' : 'Add Department'}</DialogTitle><p id="dept-form-desc" className="sr-only">Department form</p></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={deptForm.name} onChange={e => setDeptForm({...deptForm, name: e.target.value})} data-testid="dept-name-input" /></div>
            <div><Label>Description</Label><Textarea value={deptForm.description} onChange={e => setDeptForm({...deptForm, description: e.target.value})} /></div>
          </div>
          <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={() => deptMutation.mutate(deptForm)} disabled={!deptForm.name || deptMutation.isPending} data-testid="save-dept-btn">{deptMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Des Dialog */}
      <Dialog open={showDesForm} onOpenChange={v => !v && setShowDesForm(false)}>
        <DialogContent aria-describedby="des-form-desc">
          <DialogHeader><DialogTitle>{editDes ? 'Edit Designation' : 'Add Designation'}</DialogTitle><p id="des-form-desc" className="sr-only">Designation form</p></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={desForm.name} onChange={e => setDesForm({...desForm, name: e.target.value})} data-testid="des-name-input" /></div>
            <div><Label>Department (optional)</Label>
              <Select value={desForm.department_id} onValueChange={v => setDesForm({...desForm, department_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={() => desMutation.mutate(desForm)} disabled={!desForm.name || desMutation.isPending} data-testid="save-des-btn">{desMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== STAFF ATTENDANCE ==============
const StaffAttendancePanel = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const now = React.useMemo(() => new Date(), []);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [selectedDate, setSelectedDate] = useState(now.toISOString().split('T')[0]);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkRecords, setBulkRecords] = useState({});

  const { data: attendance = [], isLoading } = useQuery({
    queryKey: ['hr-attendance', tenantId, month, year],
    queryFn: () => api(`/tenants/${tenantId}/hr/attendance?month=${month}&year=${year}`),
  });

  const { data: summary } = useQuery({
    queryKey: ['hr-attendance-summary', tenantId, month, year],
    queryFn: () => api(`/tenants/${tenantId}/hr/attendance/summary?month=${month}&year=${year}`),
  });

  const { data: staffData } = useQuery({
    queryKey: ['hr-staff-list', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/staff?limit=200&employment_status=active`),
  });

  const bulkMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/hr/attendance/bulk`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (d) => { toast.success(`Recorded attendance for ${d.recorded} staff`); setShowBulk(false); setBulkRecords({}); queryClient.invalidateQueries({ queryKey: ['hr-attendance'] }); queryClient.invalidateQueries({ queryKey: ['hr-attendance-summary'] }); },
    onError: (e) => toast.error(e.message),
  });

  const staffList = staffData?.staff || [];
  const stats = summary?.stats || {};

  // Build the initial bulk map ONCE, on button click — not via a recurring effect.
  // This was previously a useEffect([selectedDate, attendance]) that triggered
  // an infinite render loop because React-Query returning a new array ref
  // composed with Radix Select's setRef / useComposedRefs.
  const openBulkDialog = () => {
    const existing = (attendance || []).filter(a => a.date === selectedDate);
    const map = {};
    existing.forEach(a => { map[a.staff_id] = { status: a.status, clock_in: a.clock_in || '', clock_out: a.clock_out || '' }; });
    setBulkRecords(map);
    setShowBulk(true);
  };

  // Re-sync if user changes the date inside the open dialog.
  const onDateChange = (d) => {
    setSelectedDate(d);
    const existing = (attendance || []).filter(a => a.date === d);
    const map = {};
    existing.forEach(a => { map[a.staff_id] = { status: a.status, clock_in: a.clock_in || '', clock_out: a.clock_out || '' }; });
    setBulkRecords(map);
  };

  const saveBulk = () => {
    const records = Object.entries(bulkRecords).map(([staff_id, data]) => ({
      staff_id, status: data.status || 'present', clock_in: data.clock_in || '', clock_out: data.clock_out || '', notes: data.notes || '',
    }));
    if (records.length === 0) { toast.error('No attendance records to save'); return; }
    bulkMutation.mutate({ date: selectedDate, records });
  };

  const months = React.useMemo(() => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], []);
  const yearOptions = React.useMemo(() => [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1], [now]);

  return (
    <div className="space-y-4" data-testid="hr-attendance-panel">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2 items-center">
          <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
            <SelectContent>{yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={openBulkDialog} data-testid="mark-attendance-btn"><CheckCircle className="h-4 w-4 mr-1" /> Mark Attendance</Button>
      </div>

      {/* Monthly Summary */}
      {Object.keys(stats).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Monthly Summary - {months[month - 1]} {year}</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead><TableHead>Present</TableHead><TableHead>Late</TableHead><TableHead>Absent</TableHead><TableHead>Leave</TableHead><TableHead>Sick</TableHead><TableHead>Attendance %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(stats).map(([sid, s]) => {
                  const staff = staffList.find(st => st.id === sid);
                  return (
                    <TableRow key={sid}>
                      <TableCell className="font-medium">{staff?.full_name || sid}</TableCell>
                      <TableCell><Badge className="bg-green-100 text-green-800">{s.present}</Badge></TableCell>
                      <TableCell><Badge className="bg-amber-100 text-amber-800">{s.late}</Badge></TableCell>
                      <TableCell><Badge className="bg-red-100 text-red-800">{s.absent}</Badge></TableCell>
                      <TableCell><Badge className="bg-purple-100 text-purple-800">{s.on_leave}</Badge></TableCell>
                      <TableCell><Badge className="bg-pink-100 text-pink-800">{s.sick}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={s.attendance_pct} className="h-2 w-20" />
                          <span className="text-sm font-medium">{s.attendance_pct}%</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {isLoading && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>}

      {/* Bulk Attendance Dialog */}
      <Dialog open={showBulk} onOpenChange={v => !v && setShowBulk(false)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" aria-describedby="bulk-att-desc">
          <DialogHeader>
            <DialogTitle>Mark Staff Attendance</DialogTitle>
            <p id="bulk-att-desc" className="text-sm text-slate-500">Date: <Input type="date" value={selectedDate} onChange={e => onDateChange(e.target.value)} className="inline w-40 ml-2" /></p>
          </DialogHeader>
          <Table>
            <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead>Status</TableHead><TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead></TableRow></TableHeader>
            <TableBody>
              {staffList.map(s => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium text-sm">{s.full_name}<br /><span className="text-xs text-slate-500">{s.employee_number}</span></TableCell>
                  <TableCell>
                    <Select value={bulkRecords[s.id]?.status || undefined} onValueChange={v => setBulkRecords(prev => ({ ...prev, [s.id]: { ...prev[s.id], status: v } }))}>
                      <SelectTrigger className="w-28"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{ATT_STATUSES.map(st => <SelectItem key={st} value={st} className="capitalize">{st.replace('_', ' ')}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><Input type="time" value={bulkRecords[s.id]?.clock_in || ''} onChange={e => setBulkRecords(prev => ({ ...prev, [s.id]: { ...prev[s.id], clock_in: e.target.value } }))} className="w-28" /></TableCell>
                  <TableCell><Input type="time" value={bulkRecords[s.id]?.clock_out || ''} onChange={e => setBulkRecords(prev => ({ ...prev, [s.id]: { ...prev[s.id], clock_out: e.target.value } }))} className="w-28" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={saveBulk} disabled={bulkMutation.isPending} data-testid="save-bulk-attendance-btn">
              {bulkMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save Attendance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== LEAVE MANAGEMENT ==============
const LeaveManagement = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('requests');
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [editType, setEditType] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [leaveForm, setLeaveForm] = useState({ staff_id: '', leave_type_id: '', start_date: '', end_date: '', reason: '' });
  const [typeForm, setTypeForm] = useState({ name: '', max_days_per_year: 20, is_paid: true, requires_approval: true, is_active: true });

  const { data: requests = [] } = useQuery({
    queryKey: ['hr-leave-requests', tenantId, statusFilter],
    queryFn: () => api(`/tenants/${tenantId}/hr/leave-requests${statusFilter ? `?status=${statusFilter}` : ''}`),
  });

  const { data: leaveTypes = [] } = useQuery({
    queryKey: ['hr-leave-types', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/leave-types`),
  });

  const { data: staffData } = useQuery({
    queryKey: ['hr-staff-list', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/staff?limit=200`),
  });

  const applyMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/hr/leave-requests`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: (d) => { toast.success(`Leave request created (${d.days_count} days)`); setShowApplyForm(false); setLeaveForm({ staff_id: '', leave_type_id: '', start_date: '', end_date: '', reason: '' }); queryClient.invalidateQueries({ queryKey: ['hr-leave-requests'] }); },
    onError: (e) => toast.error(e.message),
  });

  const actionMutation = useMutation({
    mutationFn: ({ id, action, comment }) => api(`/tenants/${tenantId}/hr/leave-requests/${id}/action`, { method: 'PUT', body: JSON.stringify({ action, comment }) }),
    onSuccess: (d) => { toast.success(`Leave ${d.status}`); queryClient.invalidateQueries({ queryKey: ['hr-leave-requests'] }); queryClient.invalidateQueries({ queryKey: ['hr-attendance'] }); },
    onError: (e) => toast.error(e.message),
  });

  const typeMutation = useMutation({
    mutationFn: (data) => editType
      ? api(`/tenants/${tenantId}/hr/leave-types/${editType.id}`, { method: 'PUT', body: JSON.stringify(data) })
      : api(`/tenants/${tenantId}/hr/leave-types`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success(editType ? 'Leave type updated' : 'Leave type created'); setShowTypeForm(false); setEditType(null); queryClient.invalidateQueries({ queryKey: ['hr-leave-types'] }); },
    onError: (e) => toast.error(e.message),
  });

  const staffList = staffData?.staff || [];
  const STATUS_BADGE = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800' };

  return (
    <div className="space-y-4" data-testid="hr-leave-panel">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between">
          <TabsList><TabsTrigger value="requests">Requests</TabsTrigger><TabsTrigger value="types">Leave Types</TabsTrigger></TabsList>
          {tab === 'requests' && <Button size="sm" onClick={() => setShowApplyForm(true)} data-testid="apply-leave-btn"><Plus className="h-4 w-4 mr-1" /> Apply Leave</Button>}
          {tab === 'types' && <Button size="sm" onClick={() => { setEditType(null); setTypeForm({ name: '', max_days_per_year: 20, is_paid: true, requires_approval: true, is_active: true }); setShowTypeForm(true); }} data-testid="add-leave-type-btn"><Plus className="h-4 w-4 mr-1" /> Add Type</Button>}
        </div>

        <TabsContent value="requests">
          <div className="mb-3">
            <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
            </Select>
          </div>
          <Card>
            <Table>
              <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead>Leave Type</TableHead><TableHead>Period</TableHead><TableHead>Days</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No leave requests</TableCell></TableRow>
                ) : requests.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.staff_name}</TableCell>
                    <TableCell>{r.leave_type_name}</TableCell>
                    <TableCell className="text-sm">{r.start_date} to {r.end_date}</TableCell>
                    <TableCell>{r.days_count}</TableCell>
                    <TableCell><Badge className={STATUS_BADGE[r.status]}>{r.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      {r.status === 'pending' && (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={() => actionMutation.mutate({ id: r.id, action: 'approve' })} data-testid={`approve-leave-${r.id}`}><Check className="h-3.5 w-3.5 mr-1" /> Approve</Button>
                          <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => actionMutation.mutate({ id: r.id, action: 'reject' })} data-testid={`reject-leave-${r.id}`}><X className="h-3.5 w-3.5 mr-1" /> Reject</Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="types">
          <Card>
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Max Days/Year</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {leaveTypes.map(lt => (
                  <TableRow key={lt.id}>
                    <TableCell className="font-medium">{lt.name}</TableCell>
                    <TableCell>{lt.max_days_per_year}</TableCell>
                    <TableCell>{lt.is_paid ? <Badge className="bg-green-100 text-green-800">Paid</Badge> : <Badge variant="outline">Unpaid</Badge>}</TableCell>
                    <TableCell>{lt.is_active ? <Badge className="bg-green-100 text-green-800">Active</Badge> : <Badge variant="outline" className="text-red-500">Inactive</Badge>}</TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => { setEditType(lt); setTypeForm({ name: lt.name, max_days_per_year: lt.max_days_per_year, is_paid: lt.is_paid, requires_approval: lt.requires_approval, is_active: lt.is_active }); setShowTypeForm(true); }}><Edit className="h-3.5 w-3.5" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Apply Leave Dialog */}
      <Dialog open={showApplyForm} onOpenChange={v => !v && setShowApplyForm(false)}>
        <DialogContent aria-describedby="apply-leave-desc">
          <DialogHeader><DialogTitle>Apply for Leave</DialogTitle><p id="apply-leave-desc" className="sr-only">Leave request form</p></DialogHeader>
          <div className="space-y-3">
            <div><Label>Staff *</Label>
              <Select value={leaveForm.staff_id} onValueChange={v => setLeaveForm({...leaveForm, staff_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>{staffList.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.employee_number})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Leave Type *</Label>
              <Select value={leaveForm.leave_type_id} onValueChange={v => setLeaveForm({...leaveForm, leave_type_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{leaveTypes.filter(lt => lt.is_active).map(lt => <SelectItem key={lt.id} value={lt.id}>{lt.name} ({lt.max_days_per_year}d/yr)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date *</Label><Input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm({...leaveForm, start_date: e.target.value})} /></div>
              <div><Label>End Date *</Label><Input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm({...leaveForm, end_date: e.target.value})} /></div>
            </div>
            <div><Label>Reason</Label><Textarea value={leaveForm.reason} onChange={e => setLeaveForm({...leaveForm, reason: e.target.value})} /></div>
          </div>
          <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={() => applyMutation.mutate(leaveForm)} disabled={!leaveForm.staff_id || !leaveForm.leave_type_id || !leaveForm.start_date || !leaveForm.end_date || applyMutation.isPending} data-testid="submit-leave-btn">{applyMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Type Dialog */}
      <Dialog open={showTypeForm} onOpenChange={v => !v && setShowTypeForm(false)}>
        <DialogContent aria-describedby="leave-type-desc">
          <DialogHeader><DialogTitle>{editType ? 'Edit Leave Type' : 'Add Leave Type'}</DialogTitle><p id="leave-type-desc" className="sr-only">Leave type form</p></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={typeForm.name} onChange={e => setTypeForm({...typeForm, name: e.target.value})} data-testid="leave-type-name-input" /></div>
            <div><Label>Max Days per Year</Label><Input type="number" value={typeForm.max_days_per_year} onChange={e => setTypeForm({...typeForm, max_days_per_year: parseInt(e.target.value) || 0})} /></div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2"><input type="checkbox" checked={typeForm.is_paid} onChange={e => setTypeForm({...typeForm, is_paid: e.target.checked})} /> Paid Leave</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={typeForm.requires_approval} onChange={e => setTypeForm({...typeForm, requires_approval: e.target.checked})} /> Requires Approval</label>
            </div>
          </div>
          <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={() => typeMutation.mutate(typeForm)} disabled={!typeForm.name || typeMutation.isPending} data-testid="save-leave-type-btn">{typeMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== PAYROLL ==============
const PayrollPanel = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const { data: runs = [], isLoading } = useQuery({
    queryKey: ['hr-payroll-runs', tenantId, month, year],
    queryFn: () => api(`/tenants/${tenantId}/hr/payroll/runs?month=${month}&year=${year}`),
  });

  const generateMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/hr/payroll/run`, { method: 'POST', body: JSON.stringify({ month, year }) }),
    onSuccess: (d) => { toast.success(`Payroll generated for ${d.generated} staff`); queryClient.invalidateQueries({ queryKey: ['hr-payroll-runs'] }); },
    onError: (e) => toast.error(e.message),
  });

  const approveMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/hr/payroll/approve-batch`, { method: 'POST', body: JSON.stringify({ month, year }) }),
    onSuccess: (d) => { toast.success(`${d.approved} payslips approved`); queryClient.invalidateQueries({ queryKey: ['hr-payroll-runs'] }); },
    onError: (e) => toast.error(e.message),
  });

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const totalGross = runs.reduce((s, r) => s + (r.gross_pay || 0), 0);
  const totalNet = runs.reduce((s, r) => s + (r.net_pay || 0), 0);
  const draftCount = runs.filter(r => r.status === 'draft').length;

  return (
    <div className="space-y-4" data-testid="hr-payroll-panel">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2 items-center">
          <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
            <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
            <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => generateMutation.mutate()} disabled={generateMutation.isPending} data-testid="generate-payroll-btn">
            {generateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <DollarSign className="h-4 w-4 mr-1" />}Generate Payroll
          </Button>
          {draftCount > 0 && (
            <Button onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending} data-testid="approve-payroll-btn">
              {approveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}Approve All ({draftCount})
            </Button>
          )}
        </div>
      </div>

      {runs.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Total Staff</p><p className="text-xl font-bold">{runs.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Gross Pay</p><p className="text-xl font-bold text-blue-600">${totalGross.toLocaleString()}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Net Pay</p><p className="text-xl font-bold text-green-600">${totalNet.toLocaleString()}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Draft / Approved</p><p className="text-xl font-bold">{draftCount} / {runs.length - draftCount}</p></CardContent></Card>
        </div>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead><TableHead>Basic</TableHead><TableHead>Allowances</TableHead><TableHead>Deductions</TableHead><TableHead>Net Pay</TableHead><TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : runs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No payroll runs. Click "Generate Payroll" to start.</TableCell></TableRow>
            ) : runs.map(r => (
              <TableRow key={r.id}>
                <TableCell><p className="font-medium">{r.staff_name}</p><p className="text-xs text-slate-500">{r.employee_number}</p></TableCell>
                <TableCell>${(r.basic_salary || 0).toLocaleString()}</TableCell>
                <TableCell className="text-green-600">${(r.total_allowances || 0).toLocaleString()}</TableCell>
                <TableCell className="text-red-600">-${(r.total_deductions || 0).toLocaleString()}</TableCell>
                <TableCell className="font-bold">${(r.net_pay || 0).toLocaleString()}</TableCell>
                <TableCell><Badge className={r.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

// ============== PERFORMANCE ==============
const PerformancePanel = ({ tenantId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ staff_id: '', period: '', total_score: 0, grade: '', comments: '', metrics: { punctuality: 0, lesson_completion: 0, student_feedback: 0, attendance_quality: 0, grading_timeliness: 0, management_review: 0 } });

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['hr-performance', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/performance`),
  });

  const { data: staffData } = useQuery({
    queryKey: ['hr-staff-list', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/hr/staff?limit=200`),
  });

  const createMutation = useMutation({
    mutationFn: (data) => api(`/tenants/${tenantId}/hr/performance`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Review submitted'); setShowForm(false); queryClient.invalidateQueries({ queryKey: ['hr-performance'] }); },
    onError: (e) => toast.error(e.message),
  });

  const staffList = staffData?.staff || [];
  const METRIC_LABELS = { punctuality: 'Punctuality', lesson_completion: 'Lesson Completion', student_feedback: 'Student Feedback', attendance_quality: 'Attendance Quality', grading_timeliness: 'Grading Timeliness', management_review: 'Management Review' };

  const updateMetric = (key, val) => {
    const newMetrics = { ...form.metrics, [key]: val };
    const scores = Object.values(newMetrics);
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    let grade = 'D';
    if (avg >= 90) grade = 'A+';
    else if (avg >= 80) grade = 'A';
    else if (avg >= 70) grade = 'B';
    else if (avg >= 60) grade = 'C';
    setForm({ ...form, metrics: newMetrics, total_score: Math.round(avg * 10) / 10, grade });
  };

  return (
    <div className="space-y-4" data-testid="hr-performance-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Performance Reviews</h2>
        <Button size="sm" onClick={() => setShowForm(true)} data-testid="add-review-btn"><Star className="h-4 w-4 mr-1" /> New Review</Button>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead>Period</TableHead><TableHead>Score</TableHead><TableHead>Grade</TableHead></TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : reviews.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400">No performance reviews yet</TableCell></TableRow>
            ) : reviews.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.staff_name}</TableCell>
                <TableCell>{r.period}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2"><Progress value={r.total_score} className="h-2 w-20" /><span className="text-sm font-medium">{r.total_score}%</span></div>
                </TableCell>
                <TableCell><Badge className={r.grade?.startsWith('A') ? 'bg-green-100 text-green-800' : r.grade === 'B' ? 'bg-blue-100 text-blue-800' : r.grade === 'C' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>{r.grade}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showForm} onOpenChange={v => !v && setShowForm(false)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="perf-form-desc">
          <DialogHeader><DialogTitle>New Performance Review</DialogTitle><p id="perf-form-desc" className="sr-only">Performance review form</p></DialogHeader>
          <div className="space-y-4">
            <div><Label>Staff *</Label>
              <Select value={form.staff_id} onValueChange={v => setForm({...form, staff_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>{staffList.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Period *</Label><Input value={form.period} onChange={e => setForm({...form, period: e.target.value})} placeholder="e.g. Term 1 2025, Annual 2025" /></div>
            <div className="border-t pt-3 space-y-3">
              <Label className="text-base font-semibold">Metrics (0-100)</Label>
              {Object.entries(METRIC_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-3">
                  <Label className="w-40 text-sm">{label}</Label>
                  <Input type="number" min={0} max={100} className="w-20" value={form.metrics[key] || 0} onChange={e => updateMetric(key, parseInt(e.target.value) || 0)} />
                  <Progress value={form.metrics[key] || 0} className="h-2 flex-1" />
                </div>
              ))}
              <div className="flex items-center gap-3 pt-2 border-t">
                <span className="text-sm font-semibold w-40">Overall Score</span>
                <span className="text-lg font-bold">{form.total_score}%</span>
                <Badge className={form.grade?.startsWith('A') ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>{form.grade || '-'}</Badge>
              </div>
            </div>
            <div><Label>Comments</Label><Textarea value={form.comments} onChange={e => setForm({...form, comments: e.target.value})} /></div>
          </div>
          <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={() => createMutation.mutate(form)} disabled={!form.staff_id || !form.period || createMutation.isPending} data-testid="submit-review-btn">{createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Submit Review</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============== MAIN EXPORT ==============
export const SchoolHR = ({ tenantId }) => {
  const [tab, setTab] = useState('dashboard');

  const HR_TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'directory', label: 'Staff Directory', icon: Users },
    { id: 'departments', label: 'Depts & Designations', icon: Building2 },
    { id: 'attendance', label: 'Attendance', icon: Clock },
    { id: 'leave', label: 'Leave', icon: CalendarDays },
    { id: 'payroll', label: 'Payroll', icon: DollarSign },
    { id: 'performance', label: 'Performance', icon: Star },
    { id: 'intelligence', label: 'Intelligence', icon: Sparkles },
    { id: 'risk-alerts', label: 'Risk Alerts', icon: AlertTriangle },
  ];

  return (
    <div className="space-y-4" data-testid="school-hr-module">
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {HR_TABS.map(t => (
          <Button
            key={t.id}
            variant={tab === t.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setTab(t.id)}
            className="shrink-0"
            data-testid={`hr-tab-${t.id}`}
          >
            <t.icon className="h-4 w-4 mr-1" />{t.label}
          </Button>
        ))}
      </div>
      {tab === 'dashboard' && <HRDashboard tenantId={tenantId} />}
      {tab === 'directory' && <StaffDirectory tenantId={tenantId} />}
      {tab === 'departments' && <DepartmentsPanel tenantId={tenantId} />}
      {tab === 'attendance' && <StaffAttendancePanel tenantId={tenantId} />}
      {tab === 'leave' && <LeaveManagement tenantId={tenantId} />}
      {tab === 'payroll' && <PayrollPanel tenantId={tenantId} />}
      {tab === 'performance' && <PerformancePanel tenantId={tenantId} />}
      {tab === 'intelligence' && <IntelligencePanel tenantId={tenantId} />}
      {tab === 'risk-alerts' && <RiskAlertsPanel tenantId={tenantId} />}
    </div>
  );
};

export default SchoolHR;

// ==================== PHASE D — INTELLIGENCE PANEL ====================
// Components moved to HRPhaseDPanels.jsx — see top-of-file import.
